import { YAxisOptions } from "highcharts";

import { StandardDataParameter } from "@/lib/api";

export const GREEN = ["#2C600E", "#39870C", "#91B96F"];
export const BLUE = ["#154273", "#007BC7", "#8FCAE7"];

export enum LinegraphUnits {
  CANDELA_PER_SQM = "candela_per_square_meter",
  JOULE_PER_SQCM = "joule_per_square_centimeter",
  METRE_PER_SECOND = "meter_per_second",
  CELSIUS = "degree_celsius",
  SECONDS = "second",
  MINUTE = "minute",
  WATT_PER_SQM = "watt_per_square_meter",
  MM_PER_HOUR = "millimeter_per_hour",
  MM = "millimeter",
  FEET = "foot",
  METER = "meter",
  HECTOPASCAL = "hectopascal",
  PERCENT = "percent",
  OKTA = "okta",
  DEGREES = "degree",
  KNOTS = "knots",
  UNITLESS = "unitless",
}

export function* colorGenerator(colors: string[]) {
  let index = 0;
  while (true) {
    yield colors[index % colors.length];
    index++;
  }
}

export const getFormattedValue = (value: number, parameter?: StandardDataParameter) => {
  return roundingPrecision(value, parameter?.unit.type);
};

export const roundingPrecision = (value: number, unit?: string) => {
  if (typeof value === "number" && value % 1 !== 0) {
    return unit === "knots" ? Math.round(value * 10) / 10 : Number(value.toFixed(1));
  }
  return value;
};

export const getYAxisPerUnit = (key?: string): YAxisOptions[] => {
  if (key === LinegraphUnits.CELSIUS) {
    return [{ softMin: 0, softMax: 10 }];
  }
  // Rain intensity and rain sum keep a 0-1 range even when every value is 0, so the
  // axis does not collapse to a single tick.
  if (
    key === LinegraphUnits.MM_PER_HOUR ||
    key === "mm.h-1" ||
    key === LinegraphUnits.MM ||
    key === "mm"
  ) {
    return [{ min: 0, softMax: 1 }];
  }
  if (key === LinegraphUnits.PERCENT || key === "percentage") {
    return [{ min: 0, max: 100 }];
  }
  if (key === LinegraphUnits.OKTA) {
    return [
      {
        min: 0,
        max: 9,
        tickInterval: 1,
      },
    ];
  }
  if (key === LinegraphUnits.HECTOPASCAL || key === "hPa") {
    return [{ min: 950, softMax: 1090 }];
  }
  if (key === LinegraphUnits.DEGREES) {
    return [
      {
        min: 0,
        max: 360,
        tickInterval: 90,
        labels: {
          formatter: (e) => {
            switch (e.value) {
              case 90:
                return `${e.value}°`;
              case 180:
                return `${e.value}°`;
              case 270:
                return `${e.value}°`;
              case 0:
              case 360:
                return `${e.value}°`;
              default:
                return e.text ?? e.value.toString();
            }
          },
        },
        title: {
          text: "",
        },
      },
      {
        linkedTo: 0,
        opposite: true,
        title: undefined,
        labels: {
          formatter: (e) => {
            switch (e.value) {
              case 90:
                return "E";
              case 180:
                return "S";
              case 270:
                return "W";
              case 0:
              case 360:
                return "N";
              default:
                return e.text ?? e.value.toString();
            }
          },
        },
      },
    ];
  }

  if (
    [
      "foot",
      "meter",
      "second",
      "minute",
      "meter_per_second",
      "metre_per_second",
      "knot",
      "joule_per_kilogram",
      "joule_per_square_centimeter",
      "watt_per_square_meter",
      "cd_per_square_meter",
      "kilogram_per_square_meter",
      "square_meter_per_second",
      "square_meter_per_square_second",
    ].includes(key as string)
  ) {
    return [{ softMin: 0 }];
  }

  return [];
};

export const interpolateIntersection = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  threshold: number,
) => {
  // returns the x value where threshold intersects the y values
  return x1 + ((threshold - y1) * (x2 - x1)) / (y2 - y1);
};
